import React, { useEffect, useState } from 'react';
import {
    useAuthContext,
    useCartContext,
    useModalsContext,
    useStoreConfigContext,
} from '/src/providers';
import {
    Gift,
    Coupon,
    Toggle,
    TOGGLE_ICONS,
} from '/src/components';
import * as styles from './styles.scss';

const OrderSummary = ({ children }) => {
    const {
        translate,
        formatPrice,
    } = useStoreConfigContext();
    const { isAuth } = useAuthContext();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const {
        cart: {
            id: cartId,
            applied_gift_cards,
            applied_coupons,
            shipping_addresses,
            cart_points,
            cart_points: {
                allowed_to_save,
                saved_amount,
                customer_bwfo_cash_balance,
            },
            prices: {
                subtotal_excluding_tax,
                applied_taxes,
                discounts,
                grand_total,
            }
        },
        isLoadingCart,
        removeGiftCardFromCart,
        removeCouponFromCart,
        updateRewardPoints,
    } = useCartContext();
    const [points, setPoints] = useState(0);
    const { selected_shipping_method } = shipping_addresses.find(({ selected_shipping_method }) => !!selected_shipping_method) || {};
    const { amount } = selected_shipping_method || {};

    const handleSubmitPoints = e => {
        e.preventDefault();

        updateRewardPoints({ currency_amount: points }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }));
    };

    const handleChangePoints = ({ target: { value } }) => {
        const isMatchRule = /^[0-9]*(\.[0-9]{0,2})?$/.test(value);

        if (!isNaN(Number(value)) && value <= allowed_to_save.value && isMatchRule) {
            if (value.length > 1) {
                setPoints(value.replace(/(^|\s)0/g, "$1"));
            } else {
                setPoints(value);
            }
        }
    };

    useEffect(() => {
        setPoints(saved_amount.value);
    }, [cart_points]);

    return (
        <>
            <div className={styles.summary}>
                <div className={styles.item}>
                    <div className={styles.name}>
                        {translate('Price')}
                    </div>
                    <div className={styles.price}>
                        {formatPrice(subtotal_excluding_tax)}
                    </div>
                </div>
                {
                    !!applied_taxes.length &&
                    <div className={styles.item}>
                        <div className={styles.name}>
                            {translate('Taxes')}
                        </div>
                        <div className={styles.price}>
                            {
                                applied_taxes.map(({ amount }, index) => (
                                    <div key={index}>
                                        {formatPrice(amount)}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                }
                {
                    !!discounts?.length &&
                    <div className={styles.item}>
                        <div className={styles.name}>
                            {translate('Discount')}
                        </div>
                        <div className={styles.price}>
                            {
                                discounts.map(({ label, amount }, index) => (
                                    <div key={index}>
                                        {`${label} ${formatPrice(amount)}`}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                }
                {
                    !!amount &&
                    <div className={styles.item}>
                        <div className={styles.name}>
                            {translate('Delivery')}
                        </div>
                        <div className={styles.price}>
                            {formatPrice(amount)}
                        </div>
                    </div>
                }
                <Toggle
                    text={
                        <span className={!!applied_coupons?.length ? styles.used : ''}>
                            {translate('Coupon code')}
                        </span>
                    }
                    icon={TOGGLE_ICONS.COUPON}
                >
                    {
                        applied_coupons?.map(({ code }) => (
                            <div
                                key={code}
                                className={styles.del}
                            >
                                <span>{code}</span>
                                <button
                                    className={styles.remove}
                                    onClick={
                                        () => removeCouponFromCart({
                                            input: { cart_id: cartId }
                                        }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }))
                                    }
                                    disabled={isLoadingCart}
                                >
                                    <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0.171429 7.89742C0.285714 8.01171 0.4 8.06885 0.571428 8.06885C0.742857 8.06885 0.857143 8.01171 0.971428 7.89742L4 4.86885L7.02857 7.89742C7.14286 8.01171 7.31429 8.06885 7.42857 8.06885C7.54286 8.06885 7.71429 8.01171 7.82857 7.89742C8.05714 7.66885 8.05714 7.32599 7.82857 7.09742L4.8 4.06885L7.82857 1.04028C8.05714 0.811705 8.05714 0.468848 7.82857 0.240276C7.6 0.0117048 7.25714 0.0117048 7.02857 0.240276L4 3.26885L0.971428 0.240276C0.742857 0.0117048 0.4 0.0117048 0.171429 0.240276C-0.0571429 0.468848 -0.0571429 0.811705 0.171429 1.04028L3.2 4.06885L0.171429 7.09742C-0.0571429 7.32599 -0.0571429 7.66885 0.171429 7.89742Z" fill="#64665D"/>
                                    </svg>
                                </button>
                            </div>
                        ))
                    }

                    {!applied_coupons?.length && <Coupon />}
                </Toggle>
                 {
                    isAuth &&
                    <Toggle
                        text={
                            <>
                                {translate('BWFO Cash')}
                                {!!saved_amount.value && <span className={styles.saved}> ({formatPrice(saved_amount)})</span>}
                            </>
                        }
                        icon={TOGGLE_ICONS.CASH}
                    >
                        <>
                            <div
                                className={styles.available}
                                dangerouslySetInnerHTML={{
                                    __html: translate('You have available to spend %1', `<span class="${styles.saved}">${formatPrice(customer_bwfo_cash_balance)}</span>`)
                                }}
                            />
                            <form
                                className={styles.cash}
                                onSubmit={handleSubmitPoints}
                            >
                                <input
                                    type="range"
                                    value={points}
                                    min={0}
                                    max={allowed_to_save.value}
                                    step={0.01}
                                    onChange={e => setPoints(e.target.value)}
                                />
                                <div className={styles.points}>
                                    <input
                                        value={points}
                                        onChange={handleChangePoints}
                                    />
                                </div>
                                <button disabled={isLoadingCart || points === saved_amount.value}>
                                    {translate('Add')}
                                </button>
                            </form>
                        </>
                    </Toggle>
                }
                <Toggle
                    text={
                        <span className={!!applied_gift_cards.length ? styles.used : ''}>
                            {translate('Gift card')}
                        </span>
                    }
                    icon={TOGGLE_ICONS.GIFT}
                >
                    {
                        applied_gift_cards.map(({ code }) => (
                            <div
                                key={code}
                                className={styles.del}
                            >
                                <span>{code}</span>
                                <button
                                    className={styles.remove}
                                    onClick={
                                        () => removeGiftCardFromCart({
                                            input: { cart_id: cartId, gift_card_code: code }
                                        }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }))
                                    }
                                    disabled={isLoadingCart}
                                >
                                    <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0.171429 7.89742C0.285714 8.01171 0.4 8.06885 0.571428 8.06885C0.742857 8.06885 0.857143 8.01171 0.971428 7.89742L4 4.86885L7.02857 7.89742C7.14286 8.01171 7.31429 8.06885 7.42857 8.06885C7.54286 8.06885 7.71429 8.01171 7.82857 7.89742C8.05714 7.66885 8.05714 7.32599 7.82857 7.09742L4.8 4.06885L7.82857 1.04028C8.05714 0.811705 8.05714 0.468848 7.82857 0.240276C7.6 0.0117048 7.25714 0.0117048 7.02857 0.240276L4 3.26885L0.971428 0.240276C0.742857 0.0117048 0.4 0.0117048 0.171429 0.240276C-0.0571429 0.468848 -0.0571429 0.811705 0.171429 1.04028L3.2 4.06885L0.171429 7.09742C-0.0571429 7.32599 -0.0571429 7.66885 0.171429 7.89742Z" fill="#64665D"/>
                                    </svg>
                                </button>
                            </div>
                        ))
                    }
                    {!applied_gift_cards?.length && <Gift />}
                </Toggle>
                {
                    !!applied_coupons?.find(({ code }) => code === 'BWFO-GREEN5') &&
                    <div className={styles.extra}>
                        {translate('Extra £5 BWFO Cash will be added to your account.')}
                    </div>
                }
                {
                    !!applied_coupons?.find(({ code }) => code === 'BWFO-GREEN10') &&
                    <div className={styles.extra}>
                        {translate('Extra £10 BWFO Cash will be added to your account.')}
                    </div>
                }
            </div>
            <div className={styles.total}>
                <div className={styles.item}>
                    <div className={styles.name}>
                        {translate('Grand total')}
                    </div>
                    <div className={styles.grand}>
                        {formatPrice(grand_total)}
                    </div>
                </div>
                {children}
            </div>
        </>
    )
};

export default OrderSummary;
